import React, { useState, useEffect } from 'react';
import { requestPasswordReset, resetPassword } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, X, Send, Lock, Mail, Check, X as XIcon } from 'lucide-react';

const PasswordResetForm = ({ setShowResetForm }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [token, setToken] = useState('');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordCriteria, setPasswordCriteria] = useState({
        length: false,
        letter: false,
        number: false,
        special: false
    });
    const [isValidPassword, setIsValidPassword] = useState(false);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);

    useEffect(() => {
        const allCriteriaMet = Object.values(passwordCriteria).every(criteria => criteria);
        setIsValidPassword(allCriteriaMet);
    }, [passwordCriteria]);

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        
        // Longitud (máximo 35 puntos)
        if (password.length >= 8) strength += 15;
        if (password.length >= 10) strength += 10;
        if (password.length >= 12) strength += 10;

        // Letras (máximo 20 puntos)
        if (password.match(/[a-z]/)) strength += 10;
        if (password.match(/[A-Z]/)) strength += 10;

        // Números (máximo 20 puntos)
        const numbers = password.match(/\d/g) || [];
        if (numbers.length >= 1) strength += 10;
        if (numbers.length >= 3) strength += 10;

        // Caracteres especiales (máximo 25 puntos)
        const specials = password.match(/[!@#$%^&*(),.?":{}|<>]/g) || [];
        if (specials.length >= 1) strength += 15;
        if (specials.length >= 2) strength += 10;

        return strength;
    };

    const getPasswordStrengthColor = (strength) => {
        if (strength < 40) return 'bg-red-500';
        if (strength < 60) return 'bg-orange-500';
        if (strength < 80) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getPasswordStrengthText = (strength) => {
        if (strength < 40) return 'Muy débil';
        if (strength < 60) return 'Débil';
        if (strength < 80) return 'Moderada';
        if (strength < 95) return 'Fuerte';
        return 'Muy fuerte';
    };

    const validatePasswordCriteria = (password) => {
        setPasswordCriteria({
            length: password.length >= 8,
            letter: /[a-zA-Z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    };

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage(null);

        try {
            await requestPasswordReset(email);
            setMessage('Se ha enviado un correo con las instrucciones.');
            setIsEmailSent(true);
            setTimeout(() => {
                setMessage(null);
            }, 10000);
        } catch (err) {
            setError(err.message || 'Ocurrió un error al enviar el correo.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage(null);

        if (!isValidPassword) {
            setError('Por favor, asegúrate de cumplir todos los requisitos de la contraseña.');
            setIsLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            setIsLoading(false);
            return;
        }

        try {
            await resetPassword(token, newPassword);
            setMessage('Contraseña restablecida exitosamente.');
            setTimeout(() => {
                setMessage(null);
            }, 10000);
            setTimeout(() => {
                setShowResetForm(false);
                navigate('/login');
            }, 1000);
        } catch (err) {
            setError(err.message || 'Ocurrió un error al restablecer la contraseña.');
        } finally {
            setIsLoading(false);
        }
    };

    const PasswordRequirement = ({ met, text }) => (
        <div className={`flex items-center gap-2 text-xs transition-all duration-200 ${
            met ? 'hidden' : 'text-gray-400'
        }`}>
            <div className="w-3.5 h-3.5 border border-gray-300 rounded-full" />
            <span>{text}</span>
        </div>
    );

    return (
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 relative border-t-4 border-t-[#1E88E5]">
            <button
                onClick={() => setShowResetForm(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <X size={20} />
            </button>

            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    Recuperación de Contraseña
                </h2>
                <p className="text-gray-500 text-sm">
                    {!isEmailSent 
                        ? "Ingresa tu correo electrónico para recibir las instrucciones"
                        : "Ingresa el token recibido y tu nueva contraseña"
                    }
                </p>
            </div>

            {/* Mensaje de Error */}
            {error && (
                <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 animate-shake">
                    <AlertCircle size={20} />
                    <span className="text-sm">{error}</span>
                </div>
            )}

            {/* Mensaje de Éxito */}
            {message && (
                <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 text-green-600 rounded-xl border border-green-100 animate-fadeIn">
                    <Send size={20} />
                    <span className="text-sm">{message}</span>
                </div>
            )}

            {isEmailSent ? (
                <form onSubmit={handleResetPassword} className="space-y-6">
                    <div className="space-y-2">
                        <label className=" text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <Lock className="w-4 h-4 text-[#1E88E5]" />
                            Token de Recuperación
                        </label>
                        <input
                            type="text"
                            className="w-full px-4 py-3 bg-white rounded-xl border border-gray-200 focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all duration-200 outline-none text-gray-600 placeholder-gray-400"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Ingresa el token recibido"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <label className=" text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <Lock className="w-4 h-4 text-[#1E88E5]" />
                            Nueva Contraseña
                        </label>
                        <input
                            type="password"
                            className={`w-full px-4 py-3 bg-white rounded-xl border ${
                                newPassword 
                                    ? (isValidPassword ? 'border-green-400' : 'border-orange-300')
                                    : 'border-gray-200'
                            } focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all duration-200 outline-none text-gray-600`}
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setPasswordStrength(calculatePasswordStrength(e.target.value));
                                validatePasswordCriteria(e.target.value);
                            }}
                            onFocus={() => setIsPasswordFocused(true)}
                            onBlur={() => {
                                if (!newPassword) {
                                    setIsPasswordFocused(false);
                                }
                            }}
                            placeholder="••••••••"
                            required
                        />
                        
                        {/* Requisitos de contraseña */}
                        <div className={`mt-3 space-y-2 bg-gray-50 rounded-xl transition-all duration-300 ${
                            isPasswordFocused 
                                ? 'opacity-100 max-h-96 translate-y-0 p-4' 
                                : 'opacity-0 max-h-0 -translate-y-2 overflow-hidden p-0'
                        }`}>
                            <div className={`text-xs font-medium text-gray-500 mb-3 ${isValidPassword ? 'hidden' : ''}`}>
                                Requisitos de la contraseña:
                            </div>
                            <PasswordRequirement met={passwordCriteria.length} text="Mínimo 8 caracteres" />
                            <PasswordRequirement met={passwordCriteria.letter} text="Al menos una letra" />
                            <PasswordRequirement met={passwordCriteria.number} text="Un número" />
                            <PasswordRequirement met={passwordCriteria.special} text="Un carácter especial" />
                            {isValidPassword && (
                                <div className="flex items-center gap-2 text-xs text-green-500 animate-fadeIn">
                                    <Check size={14} />
                                    <span>Todos los requisitos cumplidos</span>
                                </div>
                            )}
                        </div>

                        {/* Indicador de fortaleza */}
                        {newPassword && (
                            <div className="mt-3 animate-fadeIn">
                                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${getPasswordStrengthColor(passwordStrength)}`}
                                        style={{ width: `${passwordStrength}%` }}
                                    />
                                </div>
                                <p className={`text-xs mt-1.5 text-right ${getPasswordStrengthColor(passwordStrength).replace('bg-', 'text-')}`}>
                                    {getPasswordStrengthText(passwordStrength)}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className=" text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <Lock className="w-4 h-4 text-[#1E88E5]" />
                            Confirmar Contraseña
                        </label>
                        <input
                            type="password"
                            className={`w-full px-4 py-3 bg-white rounded-xl border ${
                                confirmPassword 
                                    ? (confirmPassword === newPassword ? 'border-green-400' : 'border-red-300')
                                    : 'border-gray-200'
                            } focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all duration-200 outline-none text-gray-600`}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                        />
                        {confirmPassword && confirmPassword !== newPassword && (
                            <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                                <X size={12} />
                                Las contraseñas no coinciden
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        className={`w-full py-3 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                            isValidPassword && confirmPassword === newPassword
                                ? 'bg-gradient-to-r from-[#1E88E5] to-[#66BB6A] text-white shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transform hover:-translate-y-0.5'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        disabled={!isValidPassword || confirmPassword !== newPassword || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin">🔄</div>
                                <span>Procesando...</span>
                            </>
                        ) : (
                            <>
                                <span>Restablecer Contraseña</span>
                                <Send className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>
            ) : (
                <form onSubmit={handleRequestReset} className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-[#1E88E5]" />
                            Correo Electrónico
                        </label>
                        <input
                            type="email"
                            className="w-full px-4 py-3 bg-white rounded-xl border border-gray-200 focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all duration-200 outline-none text-gray-600 placeholder-gray-400"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Ingresa tu correo electrónico"
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full py-3 px-4 bg-gradient-to-r from-[#1E88E5] to-[#66BB6A] text-white rounded-xl font-medium shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200 flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin">🔄</div>
                                <span>Enviando...</span>
                            </>
                        ) : (
                            <>
                                <span>Enviar Instrucciones</span>
                                <Send className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>
            )}
        </div>
    );

};

export default PasswordResetForm;